import React, { useState, useEffect, useRef } from 'react';
import PropertyMap from '../components/PropertyMap';
import LocationSearch from '../components/LocationSearch';
import { useNavigate, Link } from 'react-router-dom';
import { MapPin, Home, Building, TrendingUp, Search, X, Filter } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://silver-chicken-493994.hostingersite.com';

const HomePage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [error, setError] = useState(null);
  const [mapViewState, setMapViewState] = useState(null);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  // Filter states
  const [filters, setFilters] = useState({
    district: '',
    city: '',
    village: '',
    locationName: '',
  });

  // Selected location from search
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Location coordinates for zoom
  const locationCoords = {
    'Chennai': { lat: 13.0827, lng: 80.2707, zoom: 11 },
    'Coimbatore': { lat: 11.0168, lng: 76.9558, zoom: 11 },
    'Madurai': { lat: 9.9252, lng: 78.1198, zoom: 11 },
    'Tiruchirappalli': { lat: 10.7905, lng: 78.7047, zoom: 11 },
    'Salem': { lat: 11.6643, lng: 78.1460, zoom: 11 },
    'default': { lat: 10.7905, lng: 78.4867, zoom: 7 }
  };

  // Initial load
  useEffect(() => {
    fetchDistricts();
    fetchProperties();
  }, []);

  // Fetch when filters change (except locationName which is handled separately)
  useEffect(() => {
    if (loading) return;
    // Don't fetch for locationName changes (handled by handleLocationSelect)
    const timer = setTimeout(() => {
      fetchProperties();
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.district, filters.city, filters.village]);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/districts`);
      setDistricts(response.data || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchProperties = async (customFilters = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const currentFilters = customFilters || filters;
      
      const params = new URLSearchParams();
      if (currentFilters.district) params.append('district', currentFilters.district);
      if (currentFilters.city) params.append('city', currentFilters.city);
      if (currentFilters.village) params.append('village', currentFilters.village);
      // ✅ Send locationName to backend
      if (currentFilters.locationName) {
        params.append('location', currentFilters.locationName);
      }
      
      const response = await axios.get(`${API_URL}/api/properties?${params}`);
      const propertiesData = response.data.properties || response.data || [];
      setProperties(propertiesData);
      
      console.log('✅ Properties fetched:', propertiesData.length, 'for filters:', currentFilters);
      
      // Auto-zoom logic
      let zoomTarget = null;
      
      // 1. If location selected from search
      if (selectedLocation) {
        zoomTarget = { 
          lat: selectedLocation.latitude, 
          lng: selectedLocation.longitude, 
          zoom: 13 
        };
      }
      // 2. Check if district match exists
      else if (currentFilters.district && locationCoords[currentFilters.district]) {
        zoomTarget = locationCoords[currentFilters.district];
      }
      // 3. If city or village is typed, calculate center from properties
      else if ((currentFilters.city || currentFilters.village) && propertiesData.length > 0) {
        const sumLat = propertiesData.reduce((sum, p) => sum + parseFloat(p.latitude), 0);
        const sumLng = propertiesData.reduce((sum, p) => sum + parseFloat(p.longitude), 0);
        const avgLat = sumLat / propertiesData.length;
        const avgLng = sumLng / propertiesData.length;
        zoomTarget = { lat: avgLat, lng: avgLng, zoom: 12 };
      }
      
      if (zoomTarget) {
        setMapViewState({
          latitude: zoomTarget.lat,
          longitude: zoomTarget.lng,
          zoom: zoomTarget.zoom
        });
      }
      
    } catch (error) {
      console.error('Error fetching properties:', error);
      setError('Failed to load properties. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle location search selection
  const handleLocationSelect = (location) => {
    console.log('📍 Location selected:', location);
    
    if (location) {
      setSelectedLocation(location);
      
      // Create new filters with location
      const newFilters = { 
        district: '',
        city: '',
        village: '',
        locationName: location.name,
      };
      
      // Update filters
      setFilters(newFilters);
      
      // Zoom the map
      setMapViewState({
        latitude: location.latitude,
        longitude: location.longitude,
        zoom: 13
      });
      
      // ✅ Fetch properties for this location
      fetchProperties(newFilters);
      
      // Close mobile search
      setIsMobileSearchOpen(false);
    }
  };

  // ✅ Handle district change
  const handleDistrictChange = (value) => {
    console.log('📍 District selected:', value);
    
    // Clear location search
    setSelectedLocation(null);
    
    const newFilters = { 
      district: value, 
      city: '', 
      village: '',
      locationName: '',
    };
    setFilters(newFilters);
    
    // Fetch properties for this district
    fetchProperties(newFilters);
  };

  // Handle city change
  const handleCityChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, city: value, village: '', locationName: '' }));
    setSelectedLocation(null);
  };

  // Handle village change
  const handleVillageChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, village: value, locationName: '' }));
    setSelectedLocation(null);
  };

  // Handle search button click
  const handleSearch = () => {
    fetchProperties();
    setIsMobileSearchOpen(false);
  };

  // ✅ Handle clear all - GO BACK TO HOME (show all Tamil Nadu)
  const handleClearAll = () => {
    console.log('📍 Clearing all filters - going home');
    
    setFilters({ district: '', city: '', village: '', locationName: '' });
    setSelectedLocation(null);
    setMapViewState({
      latitude: 10.7905,
      longitude: 78.4867,
      zoom: 7
    });
    fetchProperties({ district: '', city: '', village: '', locationName: '' });
    setIsMobileSearchOpen(false);
    setShowFilters(false);
    navigate('/');
  };

  const closeMobileSearch = () => {
    setIsMobileSearchOpen(false);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const featuredProperties = properties.slice(0, 10);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-neutral-600 font-medium">Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center bg-red-50 p-8 rounded-xl max-w-md">
          <p className="text-red-600 font-semibold">{error}</p>
          <button 
            onClick={() => fetchProperties()}
            className="mt-4 bg-primary-500 text-white px-4 py-2 rounded hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Map Section */}
      <div className="h-[50vh] md:h-[65vh] relative flex-shrink-0">
        
        {/* 🔍 SEARCH - DESKTOP */}
        <div className="hidden md:block absolute top-4 left-1/2 transform -translate-x-1/2 z-20 w-full max-w-5xl px-4">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 border border-neutral-200/50">
            <div className="flex flex-col gap-3">
              
              {/* Row 1: Location Search + Action Buttons */}
              <div className="flex flex-row gap-3">
                <div className="flex-[2]">
                  <LocationSearch 
                    onSelect={handleLocationSelect}
                    onClear={() => {
                      // ✅ Clear selected location when user starts typing new search
                      setSelectedLocation(null);
                    }}
                    placeholder="Search any location..."
                  />
                </div>
                
                <button
                  onClick={handleSearch}
                  className="px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition shadow-sm whitespace-nowrap"
                >
                  🔍 Search
                </button>
                
                <button
                  onClick={toggleFilters}
                  className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap flex items-center gap-2 ${
                    showFilters ? 'bg-primary-500 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                </button>
                
                <button
                  onClick={handleClearAll}
                  className="px-4 py-2.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-xl transition-all whitespace-nowrap"
                >
                  Clear
                </button>
              </div>
              
              {/* Row 2: Expandable Filters */}
              {showFilters && (
                <div className="flex flex-row gap-3 pt-3 border-t border-neutral-200 animate-slide-down">
                  <div className="flex-1 relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <select
                      className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white text-sm"
                      value={filters.district || ''}
                      onChange={(e) => handleDistrictChange(e.target.value)}
                    >
                      <option value="">All Districts</option>
                      {districts?.map((d) => (
                        <option key={d.district_code} value={d.district_name}>
                          {d.district_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="City..."
                      className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white text-sm"
                      value={filters.city || ''}
                      onChange={handleCityChange}
                    />
                  </div>

                  <div className="flex-1 relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Village..."
                      className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white text-sm"
                      value={filters.village || ''}
                      onChange={handleVillageChange}
                    />
                  </div>
                </div>
              )}
              
              <div className="mt-1 text-xs text-neutral-500 text-center">
                {selectedLocation && (
                  <span className="text-primary-600 font-medium">
                    📍 {selectedLocation.name} • 
                  </span>
                )}
                {filters.district && !selectedLocation && (
                  <span className="text-primary-600 font-medium">
                    📍 {filters.district} • 
                  </span>
                )}
                {' '}{properties?.length || 0} properties found
              </div>
            </div>
          </div>
        </div>

        {/* 📱 MOBILE - Search Button */}
        <div className="absolute top-4 right-4 z-30 md:hidden flex gap-2">
          <button
            onClick={toggleFilters}
            className="bg-white p-3 rounded-full shadow-lg border border-neutral-200 hover:shadow-xl transition"
          >
            <Filter className="w-5 h-5 text-neutral-600" />
          </button>
          <button
            onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
            className="bg-white p-3 rounded-full shadow-lg border border-neutral-200 hover:shadow-xl transition"
          >
            {isMobileSearchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
          </button>
        </div>

        {/* 📱 MOBILE - Search Panel */}
        {isMobileSearchOpen && (
          <div className="absolute top-16 left-0 right-0 z-30 bg-white shadow-xl mx-4 rounded-2xl p-4 border border-neutral-200 animate-slide-down md:hidden max-h-[70vh] overflow-y-auto">
            <LocationSearch 
              onSelect={(location) => {
                handleLocationSelect(location);
                if (location) {
                  setTimeout(() => {
                    setIsMobileSearchOpen(false);
                  }, 300);
                }
              }}
              onClear={() => {
                // ✅ This clears the selected location when typing on mobile
                setSelectedLocation(null);
              }}
              placeholder="Search location..."
            />
            
            {/* Mobile Filters */}
            <div className="mt-3 space-y-3">
              <select
                className="w-full pl-4 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none bg-white text-sm"
                value={filters.district || ''}
                onChange={(e) => {
                  handleDistrictChange(e.target.value);
                  setTimeout(() => {
                    setIsMobileSearchOpen(false);
                  }, 300);
                }}
              >
                <option value="">All Districts</option>
                {districts?.map((d) => (
                  <option key={d.district_code} value={d.district_name}>
                    {d.district_name}
                  </option>
                ))}
              </select>

              <input
                type="text"
                placeholder="City..."
                className="w-full pl-4 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none bg-white text-sm"
                value={filters.city || ''}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, city: e.target.value }));
                  setSelectedLocation(null);
                }}
              />

              <input
                type="text"
                placeholder="Village..."
                className="w-full pl-4 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none bg-white text-sm"
                value={filters.village || ''}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, village: e.target.value }));
                  setSelectedLocation(null);
                }}
              />

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    fetchProperties();
                    setIsMobileSearchOpen(false);
                  }}
                  className="flex-1 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition"
                >
                  🔍 Search
                </button>
                <button
                  onClick={handleClearAll}
                  className="flex-1 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100 rounded-xl transition"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Map */}
        <PropertyMap 
          properties={properties} 
          loading={loading}
          filters={filters}
          setFilters={setFilters}
          districts={districts}
          mapViewState={mapViewState}
        />

        {/* Property Count Overlay */}
        <div className="absolute bottom-2 left-2 md:bottom-4 md:left-4 z-20 bg-white/95 backdrop-blur-sm px-3 py-1.5 md:px-6 md:py-2 rounded-full shadow-lg border border-neutral-200">
          <span className="text-xs md:text-sm font-medium text-neutral-700">
            🏠 {properties.length} {properties.length === 1 ? 'property' : 'properties'}
          </span>
        </div>
      </div>

      {/* Bottom Section - Property Cards with Two Sections */}
<div className="flex-1 bg-white border-t border-neutral-200 overflow-y-auto">
  <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
    
    {/* ============================================= */}
    {/* SECTION 1: Properties in Searched Location */}
    {/* ============================================= */}
    {(selectedLocation || filters.district || filters.city || filters.village) && (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-neutral-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 md:w-6 md:h-6 text-primary-500" />
              Properties in {selectedLocation?.name || filters.district || filters.city || filters.village}
            </h2>
            <p className="text-xs md:text-sm text-neutral-500">
              {properties.length} properties found
            </p>
          </div>
        </div>

        {properties.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {properties.slice(0, 10).map((property) => (
              <div
                key={property.id}
                onClick={() => navigate(`/property/${property.property_id}`)}
                className="bg-white rounded-xl border border-neutral-200 hover:shadow-lg transition-all duration-200 cursor-pointer group overflow-hidden"
              >
                <div className="h-32 md:h-40 bg-gradient-to-br from-primary-50 to-neutral-100 flex items-center justify-center relative">
                  {property.property_type === 'flat' && <Home className="w-10 h-10 md:w-12 md:h-12 text-primary-300" />}
                  {property.property_type === 'plot' && <Building className="w-10 h-10 md:w-12 md:h-12 text-primary-300" />}
                  {property.property_type === 'commercial' && <Building className="w-10 h-10 md:w-12 md:h-12 text-primary-300" />}
                  <div className="absolute top-2 right-2 bg-primary-500 text-white text-[10px] md:text-xs font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded-full">
                    ₹{(property.selling_price / 100000).toFixed(1)}L
                  </div>
                </div>
                
                <div className="p-3 md:p-4">
                  <h3 className="font-semibold text-neutral-900 text-sm line-clamp-1 group-hover:text-primary-600 transition">
                    {property.title}
                  </h3>
                  <p className="text-neutral-500 text-xs flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {property.district}, {property.city}
                    {property.village && `, ${property.village}`}
                  </p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-neutral-500">
                    <span className="capitalize px-2 py-0.5 bg-neutral-100 rounded-full">
                      {property.property_type}
                    </span>
                    {property.bedrooms > 0 && (
                      <span>🛏️ {property.bedrooms}</span>
                    )}
                    {property.bathrooms > 0 && (
                      <span>🚿 {property.bathrooms}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-neutral-50 rounded-xl">
            <p className="text-neutral-500">No properties found in this location</p>
            <p className="text-neutral-400 text-sm">Try adjusting your search</p>
          </div>
        )}
      </div>
    )}

    {/* ============================================= */}
    {/* SECTION 2: Latest Properties */}
    {/* ============================================= */}
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-neutral-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-primary-500" />
            Latest Properties
          </h2>
          <p className="text-xs md:text-sm text-neutral-500">
            Recently added properties
          </p>
        </div>
      </div>

      {properties.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {properties.slice(0, 10).map((property) => (
            <div
              key={property.id}
              onClick={() => navigate(`/property/${property.property_id}`)}
              className="bg-white rounded-xl border border-neutral-200 hover:shadow-lg transition-all duration-200 cursor-pointer group overflow-hidden"
            >
              <div className="h-32 md:h-40 bg-gradient-to-br from-primary-50 to-neutral-100 flex items-center justify-center relative">
                {property.property_type === 'flat' && <Home className="w-10 h-10 md:w-12 md:h-12 text-primary-300" />}
                {property.property_type === 'plot' && <Building className="w-10 h-10 md:w-12 md:h-12 text-primary-300" />}
                {property.property_type === 'commercial' && <Building className="w-10 h-10 md:w-12 md:h-12 text-primary-300" />}
                <div className="absolute top-2 right-2 bg-primary-500 text-white text-[10px] md:text-xs font-bold px-1.5 py-0.5 md:px-2 md:py-1 rounded-full">
                  ₹{(property.selling_price / 100000).toFixed(1)}L
                </div>
              </div>
              
              <div className="p-3 md:p-4">
                <h3 className="font-semibold text-neutral-900 text-sm line-clamp-1 group-hover:text-primary-600 transition">
                  {property.title}
                </h3>
                <p className="text-neutral-500 text-xs flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {property.district}, {property.city}
                  {property.village && `, ${property.village}`}
                </p>
                <div className="flex items-center gap-2 mt-2 text-xs text-neutral-500">
                  <span className="capitalize px-2 py-0.5 bg-neutral-100 rounded-full">
                    {property.property_type}
                  </span>
                  {property.bedrooms > 0 && (
                    <span>🛏️ {property.bedrooms}</span>
                  )}
                  {property.bathrooms > 0 && (
                    <span>🚿 {property.bathrooms}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-neutral-50 rounded-xl">
          <Home className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">No properties found</p>
          <p className="text-neutral-400 text-sm">Check back later for new listings</p>
        </div>
      )}
    </div>

    {/* Quick Stats - Desktop Only */}
    <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
      <div className="bg-neutral-50 rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-primary-600">{properties.length}</p>
        <p className="text-sm text-neutral-500">Total Properties</p>
      </div>
      <div className="bg-neutral-50 rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-green-600">
          {properties.filter(p => p.status === 'available').length}
        </p>
        <p className="text-sm text-neutral-500">Available</p>
      </div>
      <div className="bg-neutral-50 rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-blue-600">
          {new Set(properties.map(p => p.district)).size}
        </p>
        <p className="text-sm text-neutral-500">Districts</p>
      </div>
      <div className="bg-neutral-50 rounded-xl p-4 text-center">
        <p className="text-2xl font-bold text-purple-600">
          {properties.reduce((sum, p) => sum + (p.views_count || 0), 0)}
        </p>
        <p className="text-sm text-neutral-500">Total Views</p>
      </div>
    </div>
  </div>
</div>

      <style>{`
        @keyframes slide-down {
          from { transform: translateY(-10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-down { animation: slide-down 0.2s ease-out; }
      `}</style>
    </div>
  );
};

export default HomePage;